import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const WishlistPage = () => {
  const navigate = useNavigate();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_API_URL;

  const fetchWishlist = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user.token) { navigate('/login'); return; }
      const res = await fetch(`${API_URL}/api/customer/wishlist`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      const data = await res.json();
      setWishlist(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = async (animalId) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await fetch(`${API_URL}/api/customer/wishlist/${animalId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setWishlist(prev => prev.filter(a => a.id !== animalId));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchWishlist(); }, []);

  return (
    <div className="bg-background text-on-surface font-body min-h-screen">
      <Navbar variant="default" />
      <main className="pt-28 pb-20 max-w-screen-2xl mx-auto px-6 md:px-12">
        <div className="mb-10">
          <h1 className="text-4xl font-black tracking-tight text-on-surface mb-2">
            My <span className="text-primary">Wishlist</span>
          </h1>
          <p className="text-on-surface-variant">Animals you've saved for later.</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-surface-container-lowest rounded-xl overflow-hidden animate-pulse">
                <div className="h-64 bg-surface-container-high" />
                <div className="p-6 space-y-3">
                  <div className="h-4 bg-surface-container-high rounded w-3/4" />
                  <div className="h-4 bg-surface-container-high rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : wishlist.length === 0 ? (
          <div className="text-center py-32 space-y-4">
            <span className="material-symbols-outlined text-[72px] text-outline-variant block">favorite_border</span>
            <p className="text-2xl font-bold text-on-surface-variant">No saved animals yet</p>
            <p className="text-on-surface-variant">Browse the marketplace and save animals you're interested in.</p>
            <button onClick={() => navigate('/search')}
              className="mt-4 px-8 py-3 bg-primary text-white rounded-full font-bold hover:bg-primary/90 transition-all">
              Browse Marketplace
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {wishlist.map(item => (
              <div key={item.id}
                className="bg-surface-container-lowest rounded-xl overflow-hidden editorial-shadow group border border-transparent hover:border-primary-fixed-dim/30 transition-all duration-500">
                <div className="relative h-64 overflow-hidden cursor-pointer"
                 onClick={() => navigate(item.type === 'meat' ? `/meat-product/${item.public_id || item.id}` : `/product/${item.public_id || item.id}`)}>
                  <img
                    src={item.image_url || '/assets/placeholder.png'}
                    alt={item.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-black tracking-widest uppercase text-primary">
                    {item.category}
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeFromWishlist(item.id); }}
                    className="absolute top-4 right-4 transition-colors">
                    <span
                      className="material-symbols-outlined text-2xl text-red-500"
                      style={{ fontVariationSettings: "'FILL' 1" }}
                    >
                      favorite
                    </span>
                  </button>
                </div>
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-on-surface leading-tight">{item.name}</h3>
                      <p className="text-xs font-medium text-neutral-500">{item.breed}</p>
                    </div>
                    <span className="text-2xl font-black text-primary tracking-tighter">
                      ₹{Number(item.price_inr).toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 py-4 border-y border-outline-variant/10 mb-6">
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-primary-container text-sm">fitness_center</span>
                      <span className="text-xs font-bold text-on-surface-variant">{item.weight_kg} kg</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-primary-container text-sm">calendar_month</span>
                      <span className="text-xs font-bold text-on-surface-variant">{item.age_months} Months</span>
                    </div>
                    <div className="flex items-center gap-2 col-span-2">
                      <span className="material-symbols-outlined text-primary-container text-sm">location_on</span>
                      <span className="text-xs font-bold text-on-surface-variant">
                        {item.location_city}{item.location_state ? `, ${item.location_state}` : ''}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(item.type === 'meat' ? `/meat-product/${item.public_id || item.id}` : `/product/${item.public_id || item.id}`)}
                    className="w-full py-4 bg-gradient-to-r from-primary to-primary-container text-white rounded-full font-bold text-sm tracking-wide shadow-lg shadow-primary/20 active:scale-95 transition-all hover:shadow-primary/40">
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <Footer variant="default" />
    </div>
  );
};

export default WishlistPage;